import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import s from './WorkTracker.module.scss';
import { personShape } from './propTypes';
import { PRIORITIES } from './lib/sorting';

export default function NewTaskPanel({
  prefill, people, currentUserId, onClose, onCreate,
}) {
  const [fields, setFields] = useState({
    title: prefill?.title || '',
    priority: 'normal',
    due_on: prefill?.due_on || '',
    assignee_id: currentUserId || '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    titleRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!fields.title.trim()) return;
    setSaving(true);
    const ok = await onCreate({
      ...fields,
      title: fields.title.trim(),
      assignee_id: fields.assignee_id || null,
      due_on: fields.due_on || null,
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <>
      <div className={s.backdrop} onClick={onClose} role="presentation" />
      <aside className={s.drawer} role="dialog" aria-modal="true" aria-labelledby="wt-new-title">
        <form onSubmit={submit} style={{ display: 'contents' }}>
          <div className={s.drawerHead}>
            <h2 className={s.drawerTitle} id="wt-new-title">New task</h2>
            <button type="button" className={s.close} onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className={s.drawerBody}>
            <div className={s.fields}>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-new-task-title">Title</label>
                <input
                  id="wt-new-task-title"
                  ref={titleRef}
                  className={s.input}
                  value={fields.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="What needs doing?"
                  required
                />
              </div>
              <div className={s.fieldRow}>
                <div className={s.field}>
                  <label className={s.label} htmlFor="wt-new-priority">Priority</label>
                  <select
                    id="wt-new-priority"
                    className={s.select}
                    value={fields.priority}
                    onChange={(e) => set('priority', e.target.value)}
                  >
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className={s.field}>
                  <label className={s.label} htmlFor="wt-new-due">Due date</label>
                  <input
                    id="wt-new-due"
                    type="date"
                    className={s.input}
                    value={fields.due_on}
                    onChange={(e) => set('due_on', e.target.value)}
                  />
                </div>
                <div className={s.field}>
                  <label className={s.label} htmlFor="wt-new-assignee">Assigned to</label>
                  <select
                    id="wt-new-assignee"
                    className={s.select}
                    value={fields.assignee_id}
                    onChange={(e) => set('assignee_id', e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-new-description">Description</label>
                <textarea
                  id="wt-new-description"
                  className={s.textarea}
                  value={fields.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Context, steps, who asked for it"
                />
              </div>
            </div>
            <div className={s.drawerFoot}>
              <button type="button" className={s.btn} onClick={onClose}>Cancel</button>
              <button type="submit" className={s.btnPrimary} disabled={saving || !fields.title.trim()}>Add task</button>
            </div>
          </div>
        </form>
      </aside>
    </>
  );
}

NewTaskPanel.propTypes = {
  prefill: PropTypes.shape({ title: PropTypes.string, due_on: PropTypes.string }),
  people: PropTypes.arrayOf(personShape).isRequired,
  currentUserId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};
NewTaskPanel.defaultProps = { prefill: null, currentUserId: null };
